import { Component, ElementRef, ViewChild } from "@angular/core";

interface ExperienceRole {
  title: string;
  note?: string;
  time: string;
  location?: string;
}

interface ExperiencePanel {
  id: string;
  label: string;
  company: string;
  logo?: string;
  initials?: string;
  roles: ExperienceRole[];
  description: string;
  featuredPublication?: {
    label: string;
    title: string;
    url: string;
  };
}

@Component({
  selector: "app-work",
  templateUrl: "./work.component.html",
  styleUrls: ["./work.component.css"],
  standalone: false,
})
export class WorkComponent {
  @ViewChild("workTabs") workTabs?: ElementRef<HTMLElement>;

  activeExperienceId = "spotify";

  experiences: ExperiencePanel[] = [
    {
      id: "spotify",
      label: "Spotify",
      company: "Spotify",
      logo: "assets/logos/spotify.png",
      roles: [
        {
          title: "Senior Machine Learning Engineer",
          note: "Promoted from Machine Learning Engineer II",
          time: "November 2021 - present",
        },
      ],
      description:
        "I work on Spotify Search, building retrieval and ranking systems for personalized search and prompt suggestions. Recent work includes zero-query recommendations, LLM-personalized suggested prompts and queries, offline evaluation with LLM-as-judge methods, and the production pipelines and services that support model training and serving.",
      featuredPublication: {
        label: "CHIIR 2026",
        title:
          "PulseSearch: Modeling Long- and Short-term Patterns for Personalized Music Search Suggestions",
        url: "https://doi.org/10.1145/3786304.3787924",
      },
    },
    {
      id: "microsoft",
      label: "Microsoft",
      company: "Microsoft",
      logo: "assets/logos/microsoft.png",
      roles: [
        {
          title: "Software Engineer II - Search ML",
          time: "September 2019 - October 2021",
        },
        {
          title: "Software Engineer",
          time: "June 2017 - August 2018",
        },
        {
          title: "Software Engineer Intern",
          time: "July 2015 - June 2016",
        },
      ],
      description:
        "I worked on search and recommendation experiences across Microsoft Search, Outlook Web, Windows Search, Cortana, Edge, Bing, and Microsoft Graph. My work included ML-backed ranking, on-device and hybrid search, expertise discovery, autosuggest experiences, and people suggestion relevance.",
    },
    {
      id: "factmata",
      label: "Factmata",
      company: "Factmata",
      logo: "assets/logos/factmata.jpg",
      roles: [
        {
          title: "Machine Learning Engineer",
          time: "June 2019 - August 2019",
        },
      ],
      description:
        "I worked on abusive speech detection using machine learning and NLP as part of my MSc thesis research. The work focused on text classification, dataset preparation, model evaluation, and applying language modelling techniques to moderation problems.",
    },
    {
      id: "intelligence",
      label: "Intelligence Group",
      company: "Intelligence Group - AUTh",
      logo: "assets/logos/intelligence.png",
      roles: [
        {
          title: "Student Intern",
          time: "January 2017 - May 2017",
        },
      ],
      description:
        "I worked on learning-to-rank methods for biomedical document retrieval, with a focus on total recall optimization for systematic-review screening. The work combined machine learning implementation, retrieval evaluation, and publication-oriented research.",
      featuredPublication: {
        label: "CLEF 2017",
        title:
          "Combining Inter-Review Learning-to-Rank and Intra-Review Incremental Training for Title and Abstract Screening in Systematic Reviews",
        url: "https://intelligence.csd.auth.gr/publications/anagnostou-clef-2017.pdf",
      },
    },
    {
      id: "epfl",
      label: "EPFL",
      company: "DIAS Lab - EPFL",
      logo: "assets/logos/epfl.png",
      roles: [
        {
          title: "Student Intern",
          time: "July 2016 - September 2016",
        },
      ],
      description:
        "I worked on in-situ data exploration methods at DIAS Lab, implementing and evaluating database systems research ideas, featured in a demonstration paper with a SIGMOD 2017 publication.",
      featuredPublication: {
        label: "SIGMOD 2017",
        title: "Alpine: Efficient In-Situ Data Exploration in the Presence of Updates",
        url: "https://dl.acm.org/doi/10.1145/3035918.3058732",
      },
    },
    {
      id: "itauth",
      label: "IT Center of AUTh",
      company: "IT Center of AUTh",
      logo: "assets/logos/itauth.png",
      roles: [
        {
          title: "Software Engineer Intern",
          time: "September 2013 - June 2015",
        },
      ],
      description:
        "I worked on full-stack and mobile engineering projects for Aristotle University technical systems. The role covered implementation, internal tooling, maintenance, and support for university staff and members.",
    },
    {
      id: "berkeley",
      label: "UC Berkeley CREST Lab",
      company: "UC Berkeley CREST Lab",
      initials: "UC",
      roles: [
        {
          title: "Full-stack and Mobile Engineering",
          time: "2013 - 2016",
          location: "Remote",
        },
      ],
      description:
        "I worked on an iOS mobile app for gamifying household energy savings in a research lab environment.",
    },
  ];

  get activeExperience(): ExperiencePanel {
    return (
      this.experiences.find(
        (experience) => experience.id === this.activeExperienceId
      ) || this.experiences[0]
    );
  }

  get activeExperienceIndex(): number {
    const activeIndex = this.experiences.findIndex(
      (experience) => experience.id === this.activeExperienceId
    );

    return activeIndex >= 0 ? activeIndex : 0;
  }

  getExperienceDistance(index: number): number {
    return Math.abs(index - this.activeExperienceIndex);
  }

  setActiveExperience(experienceId: string, event?: Event): void {
    this.activeExperienceId = experienceId;

    (event?.currentTarget as HTMLElement | undefined)?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }

  scrollTabs(direction: "left" | "right"): void {
    const tabList = this.workTabs?.nativeElement;

    if (!tabList) {
      return;
    }

    const scrollAmount = Math.max(tabList.clientWidth * 0.7, 160);

    tabList.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  }
}
